import { safeSync } from "@perceptron/utils/errorHandling";
import { DocumentVersion } from "@perceptron/utils/types";
import { Editor } from "@tiptap/core";

import { useMediaStore } from "@demo/store/media";
import { Role } from "@demo/types/base";
import { focusParagraphAfterInsertedMedia } from "@demo/utils/editor";
import { createMediaSetContainerNode } from "@demo/utils/media-set-factory";

/**
 * Creates a draft media set for `role` and inserts an empty mediaSetContainer for it: at `pos` when given, otherwise
 * at the current selection. Shared by every "insert a new media container" path so they all build the node through
 * `createMediaSetContainerNode` and register the set under the editor's role.
 *
 * Returns the new set id, or null when the editor has been destroyed or the insert throws (in which case the
 * just-created set is removed so the store isn't left with a set that has no container).
 */
export function insertMediaSet(editor: Editor, role: Role, pos?: number): string | null {
  if (editor.isDestroyed) return null;

  const { addMediaSet, removeMediaSet } = useMediaStore.getState();
  const setId = addMediaSet(role, DocumentVersion.Draft);
  const node = createMediaSetContainerNode(setId);

  const inserted = safeSync(
    () => {
      const chain = editor.chain().focus();
      (pos === undefined ? chain.insertContent(node) : chain.insertContentAt(pos, node)).run();
      return true;
    },
    false,
    "insertMediaSet",
  );

  if (!inserted) {
    removeMediaSet(setId);
    return null;
  }

  // Inserting an atom leaves it selected, and a selected media container paints
  // as a stray sliver of highlight beside a block whose own content is
  // `select-none` — it reads as empty text that somehow got selected. Land the
  // cursor in the paragraph after it instead, which is where typing continues
  // anyway. The same helper runs after an upload, for the same reason.
  focusParagraphAfterInsertedMedia(editor, setId);

  return setId;
}
